import React, { memo, FC, useMemo } from 'react';
import cn from 'classnames';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import { renderToString } from 'react-dom/server';
import ReactECharts from 'echarts-for-react';
import { EChartsOption, TooltipComponentFormatterCallbackParams } from 'echarts';
import { CallbackDataParams } from 'echarts/types/dist/shared';
import { formatMoney, hexToRgba } from 'src/helpers';
import { GroupBy } from 'src/constants/statisticsConstants';
import { TStatisticsColumnMappedChartProps, TGetChartConfigPayload } from './types';
import { statisticChunkGroupper, statisticMappedChartSerializer } from 'src/helpers/statisticsHelpers';

type TMappedTooltipPoint = {
  rawValue: number;
  percentage: number;
  value: number;
};

const StatisticsColumnMappedChart: FC<TStatisticsColumnMappedChartProps> = memo(
  ({ data, type, currency, wrapperClassName, yAxisType, columnStackingType, colors, externalChartOptions }) => {
    const groups = useMemo(
      () => Object.keys(data).reduce((acc, key) => ({ ...acc, [key]: statisticChunkGroupper(data?.[key], type) }), {}),
      [data, type]
    );

    const mappedData = useMemo(() => statisticMappedChartSerializer(groups), [groups]);

    const options: EChartsOption = useMemo(
      () =>
        isEmpty(externalChartOptions)
          ? getChartConfig({ series: mappedData, currency, yAxisType, columnStackingType, colors })
          : merge(
              {},
              getChartConfig({ series: mappedData, currency, yAxisType, columnStackingType, colors }),
              externalChartOptions
            ),
      [mappedData, currency, yAxisType, columnStackingType, colors, externalChartOptions]
    );

    return (
      <div className={cn({ [wrapperClassName]: wrapperClassName })}>
        {/* //* current usage of conditional rendering is needed to redraw chart correctly, shouldn't change it. */}
        {type === GroupBy.DAY && <ReactECharts option={options} style={{ width: '100%', height: 400 }} />}
        {type === GroupBy.MONTH && <ReactECharts option={options} style={{ width: '100%', height: 400 }} />}
        {type === GroupBy.WEEK && <ReactECharts option={options} style={{ width: '100%', height: 400 }} />}
        {type === GroupBy.YEAR && <ReactECharts option={options} style={{ width: '100%', height: 400 }} />}
        {typeof type === 'undefined' && <ReactECharts option={options} style={{ width: '100%', height: 400 }} />}
      </div>
    );
  }
);

const getChartConfig = ({
  series,
  currency,
  yAxisType = 'linear',
  columnStackingType = 'normal',
  colors = ['#077186', '#69B6BE']
}: TGetChartConfigPayload): EChartsOption => {
  const axisType = yAxisType === 'logarithmic' ? 'log' : 'value';
  const dates = series?.[0]?.dates ?? [];

  const totalsByIndex = dates.map((_, dateIndex) =>
    series.reduce((acc, item) => acc + (item.data?.[dateIndex] ?? 0), 0)
  );

  const mappedSeries = series.map((item) => ({
    name: item.name,
    type: 'bar' as const,
    stack: 'total',
    barMinHeight: 0,
    data: item.data.map((rawValue, dateIndex) => {
      const total = totalsByIndex[dateIndex] ?? 0;
      const percentage = total > 0 ? (rawValue / total) * 100 : 0;

      return {
        value: columnStackingType === 'percent' ? Number(percentage.toFixed(6)) : rawValue,
        rawValue,
        percentage
      };
    })
  }));

  const legendSeriesOrder = mappedSeries.map((item) => item.name);
  const colorBySeriesName = new Map(series.map((item, index) => [item.name, colors[index] ?? colors[0]]));
  const stackedSeries = [...mappedSeries].reverse().map((item) => ({
    ...item,
    itemStyle: {
      color: colorBySeriesName.get(item.name)
    }
  }));

  return {
    color: colors,
    grid: { left: 16, right: 16, top: 16, bottom: 72, containLabel: true },
    xAxis: {
      type: 'category',
      axisTick: { show: true },
      axisLabel: {
        fontFamily: 'Verlag, sans-serif'
      },
      data: dates
    },
    yAxis: {
      type: axisType,
      max: columnStackingType === 'percent' ? 100 : undefined,
      axisLabel: {
        fontFamily: 'Verlag, sans-serif',
        formatter: (value: number) => `${columnStackingType === 'normal' ? formatMoney(value, currency) : value + '%'}`
      }
    },
    tooltip: {
      show: true,
      trigger: 'axis',
      backgroundColor: '#fff',
      borderColor: '#077186',
      textStyle: { color: '#111', fontFamily: 'Verlag, sans-serif' },
      extraCssText: 'white-space:normal;font-family:Verlag,sans-serif;',
      position: (point, _params, _dom, _rect, size) => {
        const pointX = Array.isArray(point) ? point[0] : 0;
        const pointY = Array.isArray(point) ? point[1] : 0;
        const tooltipWidth = size?.contentSize?.[0] ?? 0;
        const chartWidth = size?.viewSize?.[0] ?? 0;
        let tooltipX = pointX - tooltipWidth / 2;

        if (chartWidth < tooltipX + tooltipWidth * 1.01) tooltipX = pointX - tooltipWidth * 0.85;
        if (tooltipX < 0 || tooltipX < tooltipWidth * 0.02) tooltipX = pointX * 0.5;

        return [tooltipX, pointY];
      },
      formatter: (rawParams: TooltipComponentFormatterCallbackParams | TooltipComponentFormatterCallbackParams[]) => {
        const params = (Array.isArray(rawParams) ? rawParams : [rawParams]) as CallbackDataParams[];
        const pointX = params?.[0]?.dataIndex ?? 0;
        const date = dates?.[pointX];

        return renderToString(
          <div style={{ display: 'flex', flexDirection: 'column', rowGap: '3px', fontFamily: 'Verlag, sans-serif' }}>
            {date && <span style={{ fontSize: 16, fontWeight: 500, lineHeight: 1, marginBottom: '2px' }}>{date}</span>}

            {params.map((point) => {
              const pointData = point.data as TMappedTooltipPoint;

              return (
                <div key={`${point.seriesName}-${point.dataIndex}`}>
                  <span style={{ color: point.color as string, lineHeight: 1 }}>●</span>
                  &nbsp;
                  <span
                    style={{
                      color: hexToRgba('#077186', 0.9),
                      fontSize: 16,
                      fontWeight: 600,
                      lineHeight: 1
                    }}
                  >
                    {formatMoney(pointData?.rawValue ?? 0, currency)}
                  </span>
                  <span style={{ fontSize: 16, fontWeight: 500, lineHeight: 1 }}>
                    :{' '}
                    <span style={{ fontSize: 16, fontWeight: 600, lineHeight: 1 }}>
                      {pointData?.percentage.toFixed(2)}%
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        );
      }
    },
    legend: {
      show: mappedSeries.length > 1,
      data: legendSeriesOrder,
      bottom: 0,
      left: 'center',
      icon: 'circle',
      itemWidth: 14,
      itemHeight: 14,
      itemGap: 30,
      textStyle: {
        color: '#31343A',
        fontSize: 12,
        fontFamily: 'Verlag, sans-serif',
        fontWeight: 600
      }
    },
    series: stackedSeries
  };
};

StatisticsColumnMappedChart.displayName = 'StatisticsColumnMappedChart';

export default StatisticsColumnMappedChart;
